import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Avatar,
  Divider,
  Stack,
  Snackbar,
  Button,
  TextField
} from '@mui/material';
import { useParams } from 'react-router-dom';
import baseURL from '../../config';
import {
  Business,
  AccountTree,
  LocationOn,
  Email,
  Phone
} from '@mui/icons-material';
import bgImage from '../../assets/shipimage.jpg';

const VesselOwnerProfile = () => {
  const { id } = useParams();
  const [vesselOwner, setVesselOwner] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchVesselOwnerDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${baseURL}/api/vesselDetails/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setVesselOwner(response.data.data);
        setFormData(response.data.data);
      } catch (err) {
        setError('Failed to fetch vessel owner details.');
        console.error('Error fetching vessel owner details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVesselOwnerDetails();
  }, [id]);

  const InfoItem = ({ icon, label, value }) => (
    <Grid item xs={12} sm={6}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{label}:</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{value || 'N/A'}</Typography>
      </Stack>
    </Grid>
  );

  const EditableInfoItem = ({ label, name, value }) => (
    <Grid item xs={12} sm={6}>
      <Stack direction="column" spacing={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{label}:</Typography>
        <TextField
          fullWidth
          variant="outlined"
          name={name}
          value={value || ''}
          onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
          sx={{
            borderRadius: '6px',
            marginTop: '8px',
          }}
        />
      </Stack>
    </Grid>
  );

  const Section = ({ title, icon, children }) => (
    <Paper elevation={4} sx={{ p: 4, mb: 5, borderRadius: 4, bgcolor: '#fefefe' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Avatar sx={{ bgcolor: '#1976d2' }}>{icon}</Avatar>
        <Typography variant="h5" color="primary" fontWeight={700}>{title}</Typography>
      </Stack>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={3}>{children}</Grid>
    </Paper>
  );

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${baseURL}/api/vesselOwner/updateVesselOwner/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVesselOwner(response.data.data);
      setEditMode(false);
      setSuccessMessage("Vessel owner details updated successfully.");
    } catch (err) {
      console.error("Failed to update:", err);
      setError("Failed to update vessel owner.");
    }
  };

  if (loading) return <CircularProgress sx={{ m: 3 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box
      sx={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 1100, width: '100%' }}>
        {/* Company Info */}
        <Section
          icon={<Business />}
          title={
            <Typography sx={{ color: "#C30B12", fontWeight: 'bold' }}>
              Company Information
            </Typography>
          }
        >

          {editMode ? (
            <>
              <EditableInfoItem label="Company Name" name="company_name" value={formData.company_name} />
              <EditableInfoItem label="Short Name" name="company_shortname" value={formData.company_shortname} />
              <EditableInfoItem label="Country" name="country" value={formData.country} />
              <EditableInfoItem label="Email" name="email" value={formData.email} />
              <EditableInfoItem label="Phone Number" name="phoneno" value={formData.phoneno} />
              <EditableInfoItem label="Address" name="address" value={formData.address} />
              <EditableInfoItem label="Company GST" name="companyGST" value={formData.companyGST} />
              <EditableInfoItem label="Company PAN" name="companyPAN" value={formData.companyPAN} />
              <EditableInfoItem label="Company VAT" name="companyVAT" value={formData.companyVAT} />
            </>
          ) : (
            <>
              <InfoItem icon={<Business fontSize="small" />} label="Company Name" value={vesselOwner.company_name} />
              <InfoItem icon={<AccountTree fontSize="small" />} label="Short Name" value={vesselOwner.company_shortname} />
              <InfoItem icon={<LocationOn fontSize="small" />} label="Country" value={vesselOwner.country} />
              <InfoItem icon={<Email fontSize="small" />} label="Email" value={vesselOwner.email} />
              <InfoItem icon={<Phone fontSize="small" />} label="Phone Number" value={vesselOwner.phoneno} />
              <InfoItem icon={<Business fontSize="small" />} label="Address" value={vesselOwner.address} />
              <InfoItem icon={<Business fontSize="small" />} label="Company GST" value={vesselOwner.companyGST} />
              <InfoItem icon={<Business fontSize="small" />} label="Company PAN" value={vesselOwner.companyPAN} />
              <InfoItem icon={<Business fontSize="small" />} label="Company VAT" value={vesselOwner.companyVAT} />
              {vesselOwner.companyDocument && (
                <InfoItem
                  icon={<Business fontSize="small" />}
                  label="Company Registration Document"
                  value={
                    <a
                      href={`${baseURL}/${vesselOwner.companyDocument.filePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'red', textDecoration: 'none' }} 
                    >
                      {vesselOwner.companyDocument.originalName}
                    </a>
                  }
                />
              )}

              {vesselOwner.panCardDocument && (
                <InfoItem
                  icon={<Business fontSize="small" />}
                  label="Company PAN Document"
                  value={
                    <a
                      href={`${baseURL}/${vesselOwner.panCardDocument.filePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'red', textDecoration: 'none' }} 
                    >
                      {vesselOwner.panCardDocument.originalName}
                    </a>
                  }
                />
              )}

              {vesselOwner.gstDocument && (
                <InfoItem
                  icon={<Business fontSize="small" />}
                  label="Company GST Document"
                  value={
                    <a
                      href={`${baseURL}/${vesselOwner.gstDocument.filePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'red', textDecoration: 'none' }} 
                    >
                      {vesselOwner.gstDocument.originalName}
                    </a>
                  }
                />
              )}

              {vesselOwner.vatDocument && (
                <InfoItem
                  icon={<Business fontSize="small" />}
                  label="Company VAT Document"
                  value={
                    <a
                      href={`${baseURL}/${vesselOwner.vatDocument.filePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'red', textDecoration: 'none' }} 
                    >
                      {vesselOwner.vatDocument.originalName}
                    </a>
                  }
                />
              )}
            </>
          )}
        </Section>

        {/* Accounts Section */}
        <Section
          title={
            <Typography sx={{ color: "#C30B12", fontWeight: 'bold' }}>
              Account Department
            </Typography>
          }
          icon={<AccountTree />}
        >

          {editMode ? (
            <>
              <EditableInfoItem label="Primary Name" name="accounts_department1" value={formData.accounts_department1} />
              <EditableInfoItem label="Primary Title" name="accounts_department11" value={formData.accounts_department11} />
              <EditableInfoItem label="Primary Email" name="accountsEmail" value={formData.accountsEmail} />
              <EditableInfoItem label="Primary Phone" name="phoneaccounts_department1" value={formData.phoneaccounts_department1} />
              <EditableInfoItem label="Secondary Title" name="anotherAccountsTitle" value={formData.anotherAccountsTitle} />
              <EditableInfoItem label="Secondary Name" name="anotherAccountsPersonName" value={formData.anotherAccountsPersonName} />
              <EditableInfoItem label="Secondary Email" name="anotherAccountsEmail" value={formData.anotherAccountsEmail} />
              <EditableInfoItem label="Secondary Phone" name="anotherAccountsPhoneNumber" value={formData.anotherAccountsPhoneNumber} />
            </>
          ) : (
            <>
              <InfoItem icon={<AccountTree fontSize="small" />} label="Primary Name" value={vesselOwner.accounts_department1 + ' ' + vesselOwner.accounts_department11} />
              <InfoItem icon={<AccountTree fontSize="small" />} label="Secondary Name" value={vesselOwner.anotherAccountsTitle + ' ' + vesselOwner.anotherAccountsPersonName} />
              <InfoItem icon={<Email fontSize="small" />} label="Primary Email" value={vesselOwner.accountsEmail} />
              <InfoItem icon={<Email fontSize="small" />} label="Secondary Email" value={vesselOwner.anotherAccountsEmail} />
              <InfoItem icon={<Phone fontSize="small" />} label="Primary Phone" value={vesselOwner.phoneaccounts_department1} />
              <InfoItem icon={<Phone fontSize="small" />} label="Secondary Phone" value={vesselOwner.anotherAccountsPhoneNumber} />

            </>
          )}
        </Section>

        {/* Edit / Save Buttons */}
        <Box textAlign="center" mt={4}>
          {!editMode ? (
            <Button variant="contained" sx={{ backgroundColor:"red"}} onClick={() => setEditMode(true)}>Edit</Button>
          ) : (
            <>
              <Button variant="contained" color="success" onClick={handleSubmit} sx={{ mr: 2 }}>Save</Button>
              <Button variant="outlined" color="secondary" onClick={() => setEditMode(false)}>Cancel</Button>
            </>
          )}
        </Box>

        {/* Snackbar for success */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={3000}
          onClose={() => setSuccessMessage('')}
          message={successMessage}
        />
      </Box>
    </Box>
  );
};

export default VesselOwnerProfile;
